import { Node, mergeAttributes } from '@tiptap/core';
/* eslint-disable-next-line @typescript-eslint/no-explicit-any */
type AnyMd = any;

/**
 * Atom block node that preserves LaTeX display math ($$...$$) verbatim.
 * Mirrors InlineMathExtension but at block level — prevents tiptap-markdown's
 * text serializer from escaping backslashes and braces inside the formula.
 *
 * Parse: markdown-it block rule → <div data-block-math="..."> → BlockMath node
 * Serialize: write $$\ncontent\n$$ directly (no escaping)
 */
export const BlockMath = Node.create({
  name: 'blockMath',
  group: 'block',
  atom: true,
  selectable: true,
  draggable: false,

  addAttributes() {
    return {
      content: {
        default: '',
        parseHTML: (el) => (el as HTMLElement).getAttribute('data-block-math') ?? '',
        renderHTML: (attrs) => ({ 'data-block-math': attrs.content as string }),
      },
    };
  },

  parseHTML() {
    return [{ tag: 'div[data-block-math]' }];
  },

  renderHTML({ node, HTMLAttributes }) {
    const tex = node.attrs.content as string;
    return ['div', mergeAttributes(HTMLAttributes, { class: 'math-block' }), `$$\n${tex}\n$$`];
  },

  addStorage() {
    return {
      markdown: {
        /* eslint-disable-next-line @typescript-eslint/no-explicit-any */
        serialize(state: any, node: any) {
          const content = node.attrs.content as string;
          state.write(`$$\n${content}\n$$`);
          state.closeBlock(node);
        },
        parse: {
          setup(md: AnyMd) {
            /* Block rule: $$\ncontent\n$$ or $$content$$ → math_block token */
            md.block.ruler.before('fence', 'math_block', (state: AnyMd, startLine: number, endLine: number, silent: boolean) => {
              const lineStart = state.bMarks[startLine] + state.tShift[startLine];
              const lineEnd = state.eMarks[startLine];

              /* Line must start with $$ */
              if (
                lineEnd - lineStart < 2 ||
                state.src.charCodeAt(lineStart) !== 0x24 ||
                state.src.charCodeAt(lineStart + 1) !== 0x24
              ) {
                return false;
              }

              /* Single-line form: $$content$$ */
              const afterMarker = state.src.slice(lineStart + 2, lineEnd);
              if (afterMarker.endsWith('$$') && afterMarker.length > 2) {
                if (silent) return true;
                const token = state.push('math_block', '', 0);
                token.content = afterMarker.slice(0, -2).trim();
                token.map = [startLine, startLine + 1];
                state.line = startLine + 1;
                return true;
              }

              /* Multi-line form: look for closing $$ on its own line */
              let nextLine = startLine + 1;
              let found = false;
              while (nextLine < endLine) {
                const ls = state.bMarks[nextLine] + state.tShift[nextLine];
                const le = state.eMarks[nextLine];
                const line = state.src.slice(ls, le).trimEnd();
                if (line === '$$') {
                  found = true;
                  break;
                }
                nextLine++;
              }
              if (!found) return false;
              if (silent) return true;

              const lines: string[] = [];
              for (let i = startLine + 1; i < nextLine; i++) {
                lines.push(state.src.slice(state.bMarks[i], state.eMarks[i]));
              }

              const token = state.push('math_block', '', 0);
              token.content = lines.join('\n');
              token.map = [startLine, nextLine + 1];
              state.line = nextLine + 1;
              return true;
            });

            /* Renderer: output a div with data-block-math attribute */
            md.renderer.rules['math_block'] = (tokens: AnyMd[], idx: number) => {
              const content = tokens[idx].content as string;
              const escaped = content.replace(/&/g, '&amp;').replace(/"/g, '&quot;');
              return `<div data-block-math="${escaped}">$$\n${content}\n$$</div>\n`;
            };
          },
        },
      },
    };
  },
});
